import React, { useState, useEffect, useCallback } from "react";
import {
  Database,
  Brain,
  Server,
  Copy,
  ExternalLink,
  RefreshCw,
  Settings,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Send,
  Plus,
} from "lucide-react";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";
import {
  facilityServicesApi,
  facilityServicesAuthApi,
  facilitiesApi,
  clientsApi,
  authFetch,
} from "../../utils/api";
import { PRODUCT_CODES, SERVICE_STATUSES } from "../../constants";

const StatusBadge = ({ value, options }) => {
  const opt = options.find((o) => o.value === value) || {
    label: value,
    color: "slate",
  };
  const colorMap = {
    green: "bg-green-100 text-green-800",
    yellow: "bg-yellow-100 text-yellow-800",
    red: "bg-red-100 text-red-800",
    slate: "bg-slate-100 text-slate-800",
  };
  return (
    <span
      className={`px-2 py-1 text-xs font-medium rounded-full ${colorMap[opt.color] || colorMap.slate}`}
    >
      {opt.label}
    </span>
  );
};

const ServiceTypeIcon = ({ type }) => {
  const config = {
    rag: {
      Icon: Database,
      color: "text-blue-600",
      bg: "bg-blue-50",
      label: "RAG",
    },
    slm: {
      Icon: Brain,
      color: "text-purple-600",
      bg: "bg-purple-50",
      label: "SLM",
    },
    private_llm: {
      Icon: Server,
      color: "text-amber-600",
      bg: "bg-amber-50",
      label: "Private LLM",
    },
    telegram_bot: {
      Icon: Send,
      color: "text-cyan-600",
      bg: "bg-cyan-50",
      label: "Telegram Bot",
    },
  };
  const c = config[type] || config.rag;
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-full ${c.bg}`}>
      <c.Icon className={`w-3.5 h-3.5 ${c.color} mr-1`} />
      <span className={`text-xs font-medium ${c.color}`}>{c.label}</span>
    </div>
  );
};

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  if (!value)
    return <span className="text-slate-400 text-xs">Not configured</span>;

  return (
    <div className="flex items-center space-x-1">
      <code className="text-xs bg-slate-100 px-2 py-0.5 rounded max-w-xs truncate block">
        {value}
      </code>
      <button
        onClick={handleCopy}
        className="p-1 text-slate-400 hover:text-blue-600 rounded transition-colors flex-shrink-0"
        title={`Copy ${label}`}
      >
        {copied ? (
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

const FacilityServiceManagement = () => {
  const [services, setServices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedService, setExpandedService] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [reloadingService, setReloadingService] = useState(null);

  const [configFormData, setConfigFormData] = useState({
    persona_name: "",
    system_prompt: "",
    topK: 5,
    cors_origins: "",
    pinecone_index: "",
    pinecone_namespace: "",
    pinecone_host: "",
    chatbot_webhook_url: "",
    loader_webhook_url: "",
    chatbot_workflow_id: "",
    loader_workflow_id: "",
  });
  const [telegramConfigData, setTelegramConfigData] = useState({
    group_id: "",
    respond_to_messages: true,
    daily_message_limit: 50,
  });
  const [configLoading, setConfigLoading] = useState(false);

  const [showNewServiceModal, setShowNewServiceModal] = useState(false);
  const [newServiceFormData, setNewServiceFormData] = useState({
    facility_id: "",
    service_type: "telegram_bot",
    status: "active",
  });
  const [newServiceLoading, setNewServiceLoading] = useState(false);
  const [allFacilities, setAllFacilities] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [servicesData, facilitiesData, allFacilitiesData] =
        await Promise.all([
          facilityServicesApi.getAll(),
          clientsApi.getAll(),
          facilitiesApi.getAll(),
        ]);
      setServices(servicesData);
      setFacilities(facilitiesData);
      setAllFacilities(allFacilitiesData);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getField = (obj, ...keys) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
    }
    return undefined;
  };

  const getFacilityName = (facilityId) => {
    const f = facilities.find(
      (fac) => (fac.facility_id || fac.facilityId || fac.id) === facilityId,
    );
    return f
      ? f.facility_name || f.facilityName || f.name || facilityId
      : facilityId;
  };

  const getConfig = (service) => {
    const config = service.config || service.Config || {};
    return typeof config === "string" ? JSON.parse(config) : config;
  };

  const handleReloadKB = async (e, service) => {
    e.stopPropagation();
    const config = getConfig(service);
    const loaderUrl = config.loader_webhook_url || config.loaderWebhookUrl;
    if (!loaderUrl) {
      alert("No loader webhook URL configured for this service.");
      return;
    }
    if (!window.confirm("Trigger a knowledge base reload for this service?"))
      return;

    setReloadingService(service.id);
    try {
      await fetch(loaderUrl, { method: "POST" });
      alert("KB reload triggered successfully.");
    } catch (error) {
      console.error("Error triggering KB reload:", error);
      alert("Failed to trigger KB reload. Check the console for details.");
    }
    setReloadingService(null);
  };

  const handleTestChatbot = (e, service) => {
    e.stopPropagation();
    const config = getConfig(service);
    const chatbotUrl = config.chatbot_webhook_url || config.chatbotWebhookUrl;
    if (chatbotUrl) {
      window.open(chatbotUrl, "_blank");
    } else {
      alert("No chatbot webhook URL configured for this service.");
    }
  };

  const handleEditConfig = async (e, service) => {
    e.stopPropagation();
    const facilityId = getField(service, "facility_id", "facilityId");
    const serviceType = getField(service, "service_type", "serviceType");
    setEditingService(service);
    setShowConfigModal(true);
    setConfigLoading(true);

    if (serviceType === "telegram_bot") {
      try {
        const data = await authFetch(
          `/telegram-config?facility_id=${encodeURIComponent(facilityId)}`,
        );
        setTelegramConfigData({
          group_id: data.group_id || "",
          respond_to_messages: data.respond_to_messages ?? true,
          daily_message_limit: data.daily_message_limit ?? 50,
        });
      } catch {
        const config = getConfig(service);
        setTelegramConfigData({
          group_id: config.group_id || "",
          respond_to_messages: config.respond_to_messages ?? true,
          daily_message_limit: config.daily_message_limit ?? 50,
        });
      } finally {
        setConfigLoading(false);
      }
      return;
    }

    try {
      const data = await authFetch(
        `/admin/rag/service-config?facility_id=${encodeURIComponent(facilityId)}`,
      );
      // data may be an array with one item or an object
      const fac = Array.isArray(data) ? data[0] : data;
      if (fac && !fac.error) {
        const cfg = fac.config || {};
        const origins = fac.allowed_origins || "";
        setConfigFormData({
          persona_name: fac.agent_name || "",
          system_prompt: fac.system_prompt || "",
          topK: cfg.top_k ?? 5,
          cors_origins: origins,
          pinecone_index:
            fac.pinecone_index || cfg.pinecone_index || cfg.pineconeIndex || "",
          pinecone_namespace:
            fac.pinecone_namespace ||
            cfg.pinecone_namespace ||
            cfg.pineconeNamespace ||
            "",
          pinecone_host: fac.pinecone_host || cfg.pinecone_host || "",
          chatbot_webhook_url: cfg.chatbot_webhook_url || cfg.webhook_url || "",
          loader_webhook_url: cfg.loader_webhook_url || "",
          chatbot_workflow_id:
            cfg.chatbot_workflow_id || cfg.n8n_workflow_id || "",
          loader_workflow_id:
            cfg.loader_workflow_id || cfg.n8n_ingest_workflow_id || "",
        });
      } else {
        // Fallback: read from already-loaded service config
        const config = getConfig(service);
        setConfigFormData({
          persona_name: config.persona_name || config.personaName || "",
          system_prompt: config.system_prompt || config.systemPrompt || "",
          topK: config.topK || config.top_k || 5,
          cors_origins: (config.cors_origins || config.corsOrigins || []).join(
            "\n",
          ),
          pinecone_index: config.pinecone_index || config.pineconeIndex || "",
          pinecone_namespace:
            config.pinecone_namespace || config.pineconeNamespace || "",
          pinecone_host: config.pinecone_host || "",
          chatbot_webhook_url:
            config.chatbot_webhook_url || config.chatbotWebhookUrl || "",
          loader_webhook_url:
            config.loader_webhook_url || config.loaderWebhookUrl || "",
          chatbot_workflow_id:
            config.chatbot_workflow_id || config.chatbotWorkflowId || "",
          loader_workflow_id:
            config.loader_workflow_id || config.loaderWorkflowId || "",
        });
      }
    } catch {
      const config = getConfig(service);
      setConfigFormData({
        persona_name: config.persona_name || config.personaName || "",
        system_prompt: config.system_prompt || config.systemPrompt || "",
        topK: config.topK || config.top_k || 5,
        cors_origins: (config.cors_origins || config.corsOrigins || []).join(
          "\n",
        ),
        pinecone_index: config.pinecone_index || config.pineconeIndex || "",
        pinecone_namespace:
          config.pinecone_namespace || config.pineconeNamespace || "",
        pinecone_host: config.pinecone_host || "",
        chatbot_webhook_url:
          config.chatbot_webhook_url || config.chatbotWebhookUrl || "",
        loader_webhook_url:
          config.loader_webhook_url || config.loaderWebhookUrl || "",
        chatbot_workflow_id:
          config.chatbot_workflow_id || config.chatbotWorkflowId || "",
        loader_workflow_id:
          config.loader_workflow_id || config.loaderWorkflowId || "",
      });
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!editingService) return;
    setLoading(true);
    const facilityId = getField(editingService, "facility_id", "facilityId");
    const serviceType = getField(editingService, "service_type", "serviceType");

    if (serviceType === "telegram_bot") {
      try {
        const result = await authFetch("/telegram-config", {
          method: "POST",
          body: JSON.stringify({
            facility_id: facilityId,
            ...telegramConfigData,
            daily_message_limit:
              parseInt(telegramConfigData.daily_message_limit) || 50,
          }),
        });
        if (!result?.error) {
          fetchData();
          setShowConfigModal(false);
          setEditingService(null);
        } else {
          alert(`Save failed: ${result.error}`);
        }
      } catch (error) {
        console.error("Error saving Telegram config:", error);
        alert("Save failed. Check console for details.");
      }
      setLoading(false);
      return;
    }

    try {
      const result = await authFetch("/admin/rag/service-config", {
        method: "PUT",
        body: JSON.stringify({
          facility_id: facilityId,
          agent_name: configFormData.persona_name,
          system_prompt: configFormData.system_prompt,
          allowed_origins: configFormData.cors_origins,
          top_k: parseInt(configFormData.topK) || 5,
          pinecone_index: configFormData.pinecone_index,
          pinecone_namespace: configFormData.pinecone_namespace,
          pinecone_host: configFormData.pinecone_host,
          chatbot_webhook_url: configFormData.chatbot_webhook_url,
          loader_webhook_url: configFormData.loader_webhook_url,
          chatbot_workflow_id: configFormData.chatbot_workflow_id,
          loader_workflow_id: configFormData.loader_workflow_id,
        }),
      });
      if (!result.error) {
        fetchData();
        setShowConfigModal(false);
        setEditingService(null);
      } else {
        console.error("Error updating config:", result.error);
        alert(`Save failed: ${result.error}`);
      }
    } catch (error) {
      console.error("Error updating config:", error);
      alert("Save failed. Check console for details.");
    }
    setLoading(false);
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    if (!newServiceFormData.facility_id) return;
    setNewServiceLoading(true);
    try {
      const result = await facilityServicesAuthApi.set({
        facility_id: newServiceFormData.facility_id,
        service_type: newServiceFormData.service_type,
        status: newServiceFormData.status,
        config: {},
      });
      if (!result?.error) {
        setShowNewServiceModal(false);
        setNewServiceFormData({
          facility_id: "",
          service_type: "telegram_bot",
          status: "active",
        });
        fetchData();
      } else {
        alert(`Failed to create service: ${result.error}`);
      }
    } catch (err) {
      console.error("Error creating service:", err);
      alert("Failed to create service. Check console for details.");
    }
    setNewServiceLoading(false);
  };

  const filteredServices = services.filter((service) => {
    const facilityId = getField(service, "facility_id", "facilityId") || "";
    const serviceType = getField(service, "service_type", "serviceType") || "";
    const facilityName = getFacilityName(facilityId);

    const matchesSearch =
      facilityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      facilityId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      serviceType.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = typeFilter === "all" || serviceType === typeFilter;
    const matchesStatus =
      statusFilter === "all" || service.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            onRefresh={fetchData}
            placeholder="Search services..."
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Types</option>
            {PRODUCT_CODES.filter((p) => p.type !== "combo").map((t) => (
              <option key={t.value} value={t.type}>
                {t.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Statuses</option>
            {SERVICE_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <PrimaryButton onClick={() => setShowNewServiceModal(true)} icon={Plus}>
          New Service
        </PrimaryButton>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        {SERVICE_STATUSES.map((status) => {
          const count = services.filter(
            (s) => s.status === status.value,
          ).length;
          const colorMap = {
            green: "border-green-200 bg-green-50",
            yellow: "border-yellow-200 bg-yellow-50",
            red: "border-red-200 bg-red-50",
            slate: "border-slate-200 bg-slate-50",
          };
          return (
            <div
              key={status.value}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                statusFilter === status.value
                  ? colorMap[status.color]
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
              onClick={() =>
                setStatusFilter(
                  statusFilter === status.value ? "all" : status.value,
                )
              }
            >
              <div className="text-2xl font-bold text-slate-800">{count}</div>
              <div className="text-xs text-slate-500">{status.label}</div>
            </div>
          );
        })}
      </div>

      {/* New Service Modal */}
      <FormModal
        title="Add Facility Service"
        isOpen={showNewServiceModal}
        onClose={() => setShowNewServiceModal(false)}
      >
        <form onSubmit={handleCreateService} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Facility
            </label>
            <select
              required
              value={newServiceFormData.facility_id}
              onChange={(e) =>
                setNewServiceFormData({
                  ...newServiceFormData,
                  facility_id: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— select facility —</option>
              {allFacilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.display_name || f.facility_name || f.facility_id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Service Type
            </label>
            <select
              value={newServiceFormData.service_type}
              onChange={(e) =>
                setNewServiceFormData({
                  ...newServiceFormData,
                  service_type: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {PRODUCT_CODES.filter((p) => p.type !== "combo").map((t) => (
                <option key={t.value} value={t.type}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Initial Status
            </label>
            <select
              value={newServiceFormData.status}
              onChange={(e) =>
                setNewServiceFormData({
                  ...newServiceFormData,
                  status: e.target.value,
                })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            >
              {SERVICE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <p className="text-xs text-slate-500">
            After creating the service, configure it from the Telegram Bot → Bot
            Config page (for Telegram) or the service edit button (for RAG).
          </p>

          <div className="flex justify-end space-x-3 pt-2">
            <SecondaryButton onClick={() => setShowNewServiceModal(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={newServiceLoading || !newServiceFormData.facility_id}
              icon={Plus}
            >
              {newServiceLoading ? "Creating..." : "Create Service"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      {/* Config Edit Modal */}
      <FormModal
        title="Edit Service Configuration"
        isOpen={showConfigModal}
        onClose={() => {
          setShowConfigModal(false);
          setEditingService(null);
        }}
      >
        {configLoading ? (
          <div className="py-8 text-center text-slate-500 text-sm">
            Loading configuration...
          </div>
        ) : editingService &&
          getField(editingService, "service_type", "serviceType") ===
            "telegram_bot" ? (
          /* ── Telegram Bot config form ── */
          <form onSubmit={handleSaveConfig} className="space-y-5">
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Bot Settings
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Telegram Group ID
                  </label>
                  <input
                    type="text"
                    value={telegramConfigData.group_id}
                    onChange={(e) =>
                      setTelegramConfigData({
                        ...telegramConfigData,
                        group_id: e.target.value,
                      })
                    }
                    placeholder="-1001234567890"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    Negative integer for supergroups (e.g. -1001234567890)
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Daily Message Limit
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={telegramConfigData.daily_message_limit}
                      onChange={(e) =>
                        setTelegramConfigData({
                          ...telegramConfigData,
                          daily_message_limit: e.target.value,
                        })
                      }
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={telegramConfigData.respond_to_messages}
                        onChange={(e) =>
                          setTelegramConfigData({
                            ...telegramConfigData,
                            respond_to_messages: e.target.checked,
                          })
                        }
                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700">
                        Respond to messages
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 pt-2">
              <SecondaryButton
                onClick={() => {
                  setShowConfigModal(false);
                  setEditingService(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading} icon={Settings}>
                {loading ? "Saving..." : "Save Configuration"}
              </PrimaryButton>
            </div>
          </form>
        ) : (
          /* ── RAG / SLM / Private LLM config form ── */
          <form onSubmit={handleSaveConfig} className="space-y-5">
            {/* Identity */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Identity
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Persona Name
                  </label>
                  <input
                    type="text"
                    value={configFormData.persona_name}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        persona_name: e.target.value,
                      })
                    }
                    placeholder="e.g. Ben, Cal, Lidy"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Top K (retrieval results)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={configFormData.topK}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        topK: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Vector Store */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Vector Store
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Pinecone Index
                  </label>
                  <input
                    type="text"
                    value={configFormData.pinecone_index}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        pinecone_index: e.target.value,
                      })
                    }
                    placeholder="e.g. hyveai-index"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Namespace
                  </label>
                  <input
                    type="text"
                    value={configFormData.pinecone_namespace}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        pinecone_namespace: e.target.value,
                      })
                    }
                    placeholder="e.g. AZLD4v1"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Pinecone Host
                </label>
                <input
                  type="url"
                  value={configFormData.pinecone_host || ""}
                  onChange={(e) =>
                    setConfigFormData({
                      ...configFormData,
                      pinecone_host: e.target.value,
                    })
                  }
                  placeholder="e.g. https://gop-xxxxx.svc.pinecone.io"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
            </div>

            {/* Endpoints */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Endpoints
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Chatbot Webhook URL
                  </label>
                  <input
                    type="url"
                    value={configFormData.chatbot_webhook_url}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        chatbot_webhook_url: e.target.value,
                      })
                    }
                    placeholder="https://n8n.example.com/webhook/lidy/FACILITY_ID"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Loader Webhook URL
                  </label>
                  <input
                    type="url"
                    value={configFormData.loader_webhook_url}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        loader_webhook_url: e.target.value,
                      })
                    }
                    placeholder="https://n8n.example.com/webhook/ingest/FACILITY_ID"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Workflows */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Workflows
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Chatbot Workflow ID
                  </label>
                  <input
                    type="text"
                    value={configFormData.chatbot_workflow_id}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        chatbot_workflow_id: e.target.value,
                      })
                    }
                    placeholder="n8n workflow ID"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Loader Workflow ID
                  </label>
                  <input
                    type="text"
                    value={configFormData.loader_workflow_id}
                    onChange={(e) =>
                      setConfigFormData({
                        ...configFormData,
                        loader_workflow_id: e.target.value,
                      })
                    }
                    placeholder="n8n workflow ID"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
              </div>
            </div>

            {/* Security */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                Security
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  CORS Origins (comma-separated)
                </label>
                <input
                  type="text"
                  value={configFormData.cors_origins}
                  onChange={(e) =>
                    setConfigFormData({
                      ...configFormData,
                      cors_origins: e.target.value,
                    })
                  }
                  placeholder="https://example.com,https://app.example.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
                />
              </div>
            </div>

            {/* System Prompt */}
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
                System Prompt
              </p>
              <textarea
                value={configFormData.system_prompt}
                onChange={(e) =>
                  setConfigFormData({
                    ...configFormData,
                    system_prompt: e.target.value,
                  })
                }
                rows={8}
                placeholder="Enter the chatbot system prompt..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-xs"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <SecondaryButton
                onClick={() => {
                  setShowConfigModal(false);
                  setEditingService(null);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={loading} icon={Settings}>
                {loading ? "Saving..." : "Save Configuration"}
              </PrimaryButton>
            </div>
          </form>
        )}
      </FormModal>

      {/* Services Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Facility
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Provisioned
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Persona
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-200">
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  Loading...
                </td>
              </tr>
            ) : filteredServices.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-4 text-center text-slate-500"
                >
                  No facility services found. Services are created when orders
                  are provisioned.
                </td>
              </tr>
            ) : (
              filteredServices.map((service) => {
                const facilityId =
                  getField(service, "facility_id", "facilityId") || "-";
                const serviceType =
                  getField(service, "service_type", "serviceType") || "rag";
                const provisionedAt = getField(
                  service,
                  "provisioned_at",
                  "provisionedAt",
                );
                const config = getConfig(service);
                const personaName =
                  config.persona_name || config.personaName || "-";
                const isExpanded = expandedService === service.id;

                return (
                  <React.Fragment key={service.id}>
                    <tr
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() =>
                        setExpandedService(isExpanded ? null : service.id)
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                        <div className="font-medium">
                          {getFacilityName(facilityId)}
                        </div>
                        <div className="text-xs text-slate-400">
                          {facilityId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <ServiceTypeIcon type={serviceType} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge
                          value={service.status}
                          options={SERVICE_STATUSES}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {provisionedAt
                          ? new Date(provisionedAt).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        {personaName}
                      </td>
                      <td
                        className="px-6 py-4 whitespace-nowrap text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex space-x-1">
                          {serviceType === "rag" &&
                            service.status === "active" && (
                              <button
                                onClick={(e) => handleReloadKB(e, service)}
                                disabled={reloadingService === service.id}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Reload Knowledge Base"
                              >
                                <RefreshCw
                                  className={`w-4 h-4 ${reloadingService === service.id ? "animate-spin" : ""}`}
                                />
                              </button>
                            )}
                          {serviceType === "rag" &&
                            service.status === "active" && (
                              <button
                                onClick={(e) => handleTestChatbot(e, service)}
                                className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                                title="Test Chatbot"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </button>
                            )}
                          <button
                            onClick={(e) => handleEditConfig(e, service)}
                            className="p-1 text-slate-600 hover:bg-slate-50 rounded transition-colors"
                            title="Edit Configuration"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-6 py-4 bg-slate-50 border-t border-slate-100"
                        >
                          <div className="grid grid-cols-2 gap-6 text-sm">
                            {/* Pinecone Config */}
                            <div>
                              <h4 className="font-medium text-slate-700 mb-3 flex items-center">
                                <Database className="w-4 h-4 mr-1 text-blue-600" />
                                Vector Store
                              </h4>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Pinecone Index
                                  </span>
                                  <span className="text-slate-700">
                                    {config.pinecone_index ||
                                      config.pineconeIndex ||
                                      "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Namespace
                                  </span>
                                  <span className="text-slate-700 font-mono text-xs">
                                    {config.pinecone_namespace ||
                                      config.pineconeNamespace ||
                                      "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Embedding Model
                                  </span>
                                  <span className="text-slate-700">
                                    {config.embedding_model ||
                                      config.embeddingModel ||
                                      "text-embedding-3-large"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Dimensions
                                  </span>
                                  <span className="text-slate-700">
                                    {config.embedding_dimensions ||
                                      config.embeddingDimensions ||
                                      "3072"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Source Registry NS
                                  </span>
                                  <span className="text-slate-700 font-mono text-xs">
                                    {service.source_registry_namespace ||
                                      service.sourceRegistryNamespace ||
                                      config.source_registry_namespace ||
                                      config.sourceRegistryNamespace ||
                                      "-"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Webhook URLs */}
                            <div>
                              <h4 className="font-medium text-slate-700 mb-3">
                                Endpoints
                              </h4>
                              <div className="space-y-3">
                                <div>
                                  <span className="text-xs text-slate-500 block mb-1">
                                    Chatbot Webhook
                                  </span>
                                  <CopyButton
                                    value={
                                      config.chatbot_webhook_url ||
                                      config.chatbotWebhookUrl
                                    }
                                    label="chatbot URL"
                                  />
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block mb-1">
                                    Loader Webhook
                                  </span>
                                  <CopyButton
                                    value={
                                      config.loader_webhook_url ||
                                      config.loaderWebhookUrl
                                    }
                                    label="loader URL"
                                  />
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block mb-1">
                                    Knowledge Base Sheet
                                  </span>
                                  {config.sheet_url || config.sheetUrl ? (
                                    <a
                                      href={config.sheet_url || config.sheetUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                                    >
                                      Open in Google Sheets{" "}
                                      <ExternalLink className="w-3 h-3 ml-1" />
                                    </a>
                                  ) : (
                                    <span className="text-slate-400 text-xs">
                                      Not configured
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Workflow Info */}
                            <div>
                              <h4 className="font-medium text-slate-700 mb-3">
                                Workflows
                              </h4>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Chatbot Workflow ID
                                  </span>
                                  <span className="text-slate-700 font-mono text-xs">
                                    {config.chatbot_workflow_id ||
                                      config.chatbotWorkflowId ||
                                      "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Loader Workflow ID
                                  </span>
                                  <span className="text-slate-700 font-mono text-xs">
                                    {config.loader_workflow_id ||
                                      config.loaderWorkflowId ||
                                      "-"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* RAG Config */}
                            <div>
                              <h4 className="font-medium text-slate-700 mb-3">
                                RAG Configuration
                              </h4>
                              <div className="space-y-2">
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Persona
                                  </span>
                                  <span className="text-slate-700">
                                    {personaName}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    Top K
                                  </span>
                                  <span className="text-slate-700">
                                    {config.topK || config.top_k || 5}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-xs text-slate-500 block">
                                    CORS Origins
                                  </span>
                                  <div className="space-y-0.5">
                                    {(
                                      config.cors_origins ||
                                      config.corsOrigins ||
                                      []
                                    ).length > 0 ? (
                                      (
                                        config.cors_origins ||
                                        config.corsOrigins ||
                                        []
                                      ).map((origin, i) => (
                                        <div
                                          key={i}
                                          className="text-xs text-slate-600 font-mono"
                                        >
                                          {origin}
                                        </div>
                                      ))
                                    ) : (
                                      <span className="text-slate-400 text-xs">
                                        None configured
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FacilityServiceManagement;
